import type { Ctx } from "../../types";
import type { Database } from "@shared/database.types";
import {
  formatDate,
  formatDateTimeRange,
  formatTime,
  formatTimestamp,
} from "../../lib";
import type { Tag } from "../_shared";
import {
  jobStatusLabel,
  payStatusLabel,
  reviewStatusLabel,
} from "../_status_labels";
import { shiftTypeLabel } from "../_shift_type_labels";

export type JobRow = Omit<
  Database["public"]["Views"]["v_jobs_list"]["Row"],
  "grades" | "subjects"
> & {
  grades: Tag[] | null;
  subjects: Tag[] | null;
};

export const jobsListSortMap: Record<string, string | string[]> = {
  start_date: "start_date",
  end_date: "end_date",
  start_time: "start_time",
  date_time: ["start_date", "start_time"],
  posted_at: "posted_at",
  status: "job_status",
  job_status: "job_status",
  review_status: "review_status",
  pay_status: "pay_status",
  pay_rate: "pay_rate",
  created_at: "created_at",
  updated_at: "updated_at",
};

export const jobsSearchCols = [
  "school_name",
  "claimed_by",
  "teacher_name",
  "grade_labels",
  "subject_labels",
];

function formatJobClass(
  job: JobRow,
  grades: Tag[],
  subjects: Tag[]
): string {
  const shiftLabel =
    job.shift_type && job.shift_type !== "custom"
      ? shiftTypeLabel(job.shift_type)
      : "";
  const gradesCsv = grades.map((g) => g.label).join(", ");
  const subjectsCsv = subjects.map((s) => s.label).join(", ");

  const prefix = shiftLabel ? `${shiftLabel}: ` : "";
  const gradesPart = gradesCsv ? `${gradesCsv} grade` : "";
  const middle = [gradesPart, subjectsCsv].filter(Boolean).join(" ");
  const suffix = job.teacher_name ? ` (${job.teacher_name})` : "";

  return `${prefix}${middle}${suffix}`;
}

export function decorateJob(job: JobRow) {
  const grades = job.grades ?? [];
  const subjects = job.subjects ?? [];
  const status =
    job.pay_status === "paid"
      ? job.pay_status
      : (job.review_status ?? job.job_status);
  return {
    ...job,
    status,
    school: job.school_id
      ? { label: job.school_name, value: job.school_id }
      : null,
    school_full_address: [
      job.school_address_line1,
      job.school_city,
      job.school_zip,
    ]
      .filter(Boolean)
      .join(", "),
    f_shift_type: shiftTypeLabel(job.shift_type),
    f_class: formatJobClass(job, grades, subjects),
    f_grades: grades.map((g) => g.label).join(", "),
    f_subjects: subjects.map((s) => s.label).join(", "),
    f_job_status: jobStatusLabel(job.job_status),
    f_review_status: reviewStatusLabel(job.review_status),
    f_pay_status: payStatusLabel(job.pay_status),
    f_start_date: formatDate(job.start_date, true),
    f_end_date: formatDate(job.end_date, true),
    f_start_time: formatTime(job.start_time),
    f_end_time: formatTime(job.end_time),
    f_date_time_range: formatDateTimeRange(
      job.start_date,
      job.end_date,
      job.start_time,
      job.end_time
    ),
    f_posted_at: formatTimestamp(job.posted_at),
    f_claimed_at: formatTimestamp(job.claim_claimed_at),
    f_created_at: formatTimestamp(job.created_at),
    f_updated_at: formatTimestamp(job.updated_at),
    f_deleted_at: formatTimestamp(job.deleted_at),
  };
}

export async function currentUserId(sb: Ctx["sb"]): Promise<string | null> {
  const { data, error } = await sb.auth.getSession();
  if (!data.session?.user) {
    console.error(error);
    return null;
  }
  return data.session.user.id;
}
